/**
 * Monitoring Configuration Types
 *
 * Configuration interfaces and default values for the monitoring infrastructure.
 */

import { ActionType, SamplingStrategy } from './enums';

import type { ActionTrackingConfig } from './interfaces';

/**
 * Feature flag configuration for monitoring
 */
export interface MonitoringFeatureFlags {
  /** Enable comprehensive monitoring */
  enableComprehensiveMonitoring: boolean;
  /** Enable performance tracking */
  enablePerformanceTracking: boolean;
  /** Enable error correlation */
  enableErrorCorrelation: boolean;
  /** Enable context enrichment */
  enableContextEnrichment: boolean;
  /** Enable workflow tracking */
  enableWorkflowTracking: boolean;
  /** Enable batching */
  enableBatching: boolean;
  /** Enable intelligent sampling */
  enableIntelligentSampling: boolean;
}

/**
 * Environment-specific configuration
 */
export interface EnvironmentConfig {
  /** Environment name */
  environment: string;
  /** Datadog application ID */
  datadogApplicationId: string;
  /** Datadog client token */
  datadogClientToken: string;
  /** API base URL */
  apiBaseUrl: string;
  /** Debug mode enabled */
  debugMode: boolean;
}

/**
 * Sampling configuration by action type
 */
export interface SamplingConfig {
  /** Default sampling rate */
  defaultRate: number;
  /** Sampling rates by action type */
  byActionType: Record<ActionType, number>;
  /** Sampling strategies by action type */
  strategies: Record<ActionType, SamplingStrategy>;
  /** High-frequency action patterns */
  highFrequencyPatterns: string[];
  /** Critical action patterns (always sample) */
  criticalPatterns: string[];
}

/**
 * Performance monitoring configuration
 */
export interface PerformanceConfig {
  /** Enable memory usage tracking */
  enableMemoryTracking: boolean;
  /** Enable CPU usage tracking */
  enableCpuTracking: boolean;
  /** Enable DOM metrics tracking */
  enableDomTracking: boolean;
  /** Enable network request tracking */
  enableNetworkTracking: boolean;
  /** Performance threshold for alerts */
  performanceThresholds: {
    /** Maximum action duration in ms */
    maxActionDuration: number;
    /** Maximum memory usage in MB */
    maxMemoryUsage: number;
    /** Maximum error rate percentage */
    maxErrorRate: number;
  };
}

/**
 * Batching configuration
 */
export interface BatchingConfig {
  /** Batch size for events */
  batchSize: number;
  /** Batch timeout in milliseconds */
  batchTimeout: number;
  /** Maximum queue size */
  maxQueueSize: number;
  /** Flush on page unload */
  flushOnUnload: boolean;
  /** Retry configuration */
  retry: {
    /** Maximum retry attempts */
    maxAttempts: number;
    /** Retry delay in milliseconds */
    delay: number;
    /** Exponential backoff multiplier */
    backoffMultiplier: number;
  };
}

/**
 * Default monitoring configuration
 */
export const DEFAULT_MONITORING_CONFIG: ActionTrackingConfig = {
  enabled:
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_ENABLE_MONITORING === 'true',
  samplingRate: parseFloat(
    process.env.REACT_APP_MONITORING_SAMPLING_RATE || '1.0',
  ),
  batchSize: parseInt(process.env.REACT_APP_MONITORING_BATCH_SIZE || '10'),
  batchTimeout: parseInt(
    process.env.REACT_APP_MONITORING_BATCH_TIMEOUT || '5000',
  ),
  enablePerformanceTracking:
    process.env.REACT_APP_ENABLE_PERFORMANCE_TRACKING !== 'false',
  enableErrorCorrelation:
    process.env.REACT_APP_ENABLE_ERROR_CORRELATION !== 'false',
  enableContextEnrichment:
    process.env.REACT_APP_ENABLE_CONTEXT_ENRICHMENT !== 'false',
  maxQueueSize: parseInt(
    process.env.REACT_APP_MONITORING_MAX_QUEUE_SIZE || '100',
  ),
  samplingStrategies: {
    [ActionType.WORKFLOW]: SamplingStrategy.ALWAYS,
    [ActionType.ACTION]: SamplingStrategy.HIGH,
    [ActionType.EVENT]: SamplingStrategy.LOW,
  },
};

/**
 * Default sampling configuration
 */
export const DEFAULT_SAMPLING_CONFIG: SamplingConfig = {
  defaultRate: 1.0,
  byActionType: {
    [ActionType.WORKFLOW]: 1.0, // 100% - critical business metrics
    [ActionType.ACTION]: 0.8, // 80% - important feature usage
    [ActionType.EVENT]: 0.1, // 10% - high volume events
  },
  strategies: {
    [ActionType.WORKFLOW]: SamplingStrategy.ALWAYS,
    [ActionType.ACTION]: SamplingStrategy.HIGH,
    [ActionType.EVENT]: SamplingStrategy.LOW,
  },
  highFrequencyPatterns: [
    'mouse_move',
    'scroll',
    'typing',
    'drag',
    'resize',
    'hover',
  ],
  criticalPatterns: [
    'error',
    'failure',
    'crash',
    'timeout',
    'workflow',
    'upload',
    'publish',
    'save',
  ],
};

/**
 * Default performance configuration
 */
export const DEFAULT_PERFORMANCE_CONFIG: PerformanceConfig = {
  enableMemoryTracking: true,
  enableCpuTracking: false, // CPU tracking can be expensive
  enableDomTracking: true,
  enableNetworkTracking: true,
  performanceThresholds: {
    maxActionDuration: 10000, // 10 seconds
    maxMemoryUsage: 100, // 100 MB
    maxErrorRate: 5, // 5%
  },
};

/**
 * Default batching configuration
 */
export const DEFAULT_BATCHING_CONFIG: BatchingConfig = {
  batchSize: 10,
  batchTimeout: 5000, // 5 seconds
  maxQueueSize: 100,
  flushOnUnload: true,
  retry: {
    maxAttempts: 3,
    delay: 1000, // 1 second
    backoffMultiplier: 2,
  },
};

/**
 * Default feature flags
 */
export const DEFAULT_FEATURE_FLAGS: MonitoringFeatureFlags = {
  enableComprehensiveMonitoring:
    process.env.REACT_APP_ENABLE_COMPREHENSIVE_MONITORING === 'true',
  enablePerformanceTracking:
    process.env.REACT_APP_ENABLE_PERFORMANCE_TRACKING !== 'false',
  enableErrorCorrelation:
    process.env.REACT_APP_ENABLE_ERROR_CORRELATION !== 'false',
  enableContextEnrichment:
    process.env.REACT_APP_ENABLE_CONTEXT_ENRICHMENT !== 'false',
  enableWorkflowTracking:
    process.env.REACT_APP_ENABLE_WORKFLOW_TRACKING !== 'false',
  enableBatching: process.env.REACT_APP_ENABLE_BATCHING !== 'false',
  enableIntelligentSampling:
    process.env.REACT_APP_ENABLE_INTELLIGENT_SAMPLING !== 'false',
};

/**
 * Configuration validation utilities
 */
export class ConfigValidator {
  /**
   * Validate monitoring configuration
   */
  static validateConfig(
    config: Partial<ActionTrackingConfig>,
  ): ActionTrackingConfig {
    return {
      ...DEFAULT_MONITORING_CONFIG,
      ...config,
      samplingRate: Math.max(
        0,
        Math.min(
          1,
          config.samplingRate || DEFAULT_MONITORING_CONFIG.samplingRate,
        ),
      ),
      batchSize: Math.max(
        1,
        config.batchSize || DEFAULT_MONITORING_CONFIG.batchSize,
      ),
      batchTimeout: Math.max(
        100,
        config.batchTimeout || DEFAULT_MONITORING_CONFIG.batchTimeout,
      ),
      maxQueueSize: Math.max(
        10,
        config.maxQueueSize || DEFAULT_MONITORING_CONFIG.maxQueueSize,
      ),
    };
  }

  /**
   * Validate sampling configuration
   */
  static validateSamplingConfig(
    config: Partial<SamplingConfig>,
  ): SamplingConfig {
    const validated = { ...DEFAULT_SAMPLING_CONFIG, ...config };

    // Ensure sampling rates are between 0 and 1
    Object.keys(validated.byActionType).forEach(key => {
      const actionType = key as ActionType;
      validated.byActionType[actionType] = Math.max(
        0,
        Math.min(1, validated.byActionType[actionType]),
      );
    });

    return validated;
  }
}
